import { useState, useEffect } from 'react';
import { useNotify } from 'react-admin';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Button,
  CircularProgress, TextField, IconButton, Autocomplete,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon  from '@mui/icons-material/Close';
import SaveIcon   from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import authFetch, { responseError } from '../utils/authFetch';
import { card, btn, inputSx, NAVY, GOLD, typo, clickableA11y } from '../styles/ds';

const UNITS = ['יחידה', 'מטר', 'ס"מ', 'ק"ג', 'גרם', 'ליטר', 'זוג', 'סט'];

// ── "Create new attribute" dialog ─────────────────────────────────────────────
const NewAttrDialog = ({ open, onClose, onCreated }) => {
  const notify = useNotify();
  const [label,  setLabel]  = useState('');
  const [key,    setKey]    = useState('');
  const [optStr, setOptStr] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setLabel(''); setKey(''); setOptStr(''); };

  const handleCreate = async () => {
    if (!key.trim() || !label.trim()) { notify('נא למלא שם ומפתח', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const opts = optStr.split(',').map(s => s.trim()).filter(Boolean);
      const res  = await authFetch('/attributes', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: key.trim(), label: label.trim(), data_type: 'text', options: opts }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה ביצירת תכונה'), { type: 'error' }); return; }
      const attr = await res.json();
      onCreated(attr);
      reset(); onClose();
    } catch { notify('שגיאה ביצירת תכונה', { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NAVY, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        תכונה חדשה
        <IconButton size="small" onClick={() => { reset(); onClose(); }} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} pt={1}>
          <TextField label="שם לתצוגה *" value={label} autoFocus fullWidth sx={inputSx}
            onChange={e => {
              setLabel(e.target.value);
              if (!key) setKey(e.target.value.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^\w_]/g, ''));
            }} />
          <TextField label="מפתח (key) *" value={key} fullWidth sx={inputSx}
            helperText="אנגלית ותחתיות בלבד, למשל: leather_color"
            onChange={e => setKey(e.target.value.replace(/\s/g, '_'))} />
          <TextField label="אפשרויות (מופרדות בפסיק)" value={optStr} fullWidth sx={inputSx}
            helperText="למשל: שחור, לבן, חום — ניתן להוסיף עוד לאחר מכן"
            onChange={e => setOptStr(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => { reset(); onClose(); }} sx={{ color: '#666' }}>ביטול</Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
          sx={{ backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
          צור תכונה
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Single linked-attribute card ───────────────────────────────────────────────
const AttrCard = ({ attr, productId, currentOptions, globalOptions, onRemove, onOptionsChange }) => {
  const notify   = useNotify();
  const [newOpt, setNewOpt] = useState('');
  const [saving, setSaving] = useState(false);

  const isOverridden = currentOptions !== null && currentOptions !== undefined;
  const options      = isOverridden ? currentOptions : (globalOptions ?? []);

  const saveOptions = async (opts) => {
    setSaving(true);
    try {
      const res = await authFetch('/attributes/product-options', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, attribute_id: attr.id, options: opts }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה בשמירת אפשרויות'), { type: 'error' }); return; }
      onOptionsChange(attr.id, opts.length > 0 ? opts : null);
    } catch { notify('שגיאה בשמירת אפשרויות', { type: 'error' }); }
    finally { setSaving(false); }
  };

  const addOption = () => {
    const val = newOpt.trim();
    if (!val || options.includes(val)) { setNewOpt(''); return; }
    saveOptions([...options, val]);
    setNewOpt('');
  };

  return (
    <Box sx={{
      border: `1.5px solid ${isOverridden ? NAVY : 'rgba(13,29,62,0.25)'}`,
      borderRadius: 2, p: 2, backgroundColor: isOverridden ? 'rgba(13,29,62,0.03)' : '#fff',
      transition: '0.15s',
    }}>
      {/* Header */}
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Stack className="crm-wrap" direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={0.3}>
          <Typography fontWeight={700} fontSize={13} color={NAVY}>{attr.label || attr.key}</Typography>
          <Typography fontSize={10} color="text.secondary" sx={{ fontFamily: 'monospace', opacity: 0.7 }}>
            {attr.key}
          </Typography>
          {isOverridden && (
            <Chip label="אפשרויות מותאמות" size="small"
              sx={{ fontSize: 9, height: 15, backgroundColor: '#e8f4fd', color: '#0d47a1', fontWeight: 700 }} />
          )}
        </Stack>
        <Tooltip title="הסר תכונה מקטגוריה זו">
          <IconButton size="small" onClick={onRemove} sx={{ color: '#b71c1c', opacity: 0.7, '&:hover': { opacity: 1 } }}>
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Options chips */}
      <Stack className="crm-wrap" direction="row" flexWrap="wrap" gap={0.5} mb={1} minHeight={26}>
        {options.length > 0
          ? options.map(o => (
              <Chip key={o} label={o} size="small"
                onDelete={() => saveOptions(options.filter(x => x !== o))}
                deleteIcon={<DeleteIcon sx={{ fontSize: 12 }} />}
                sx={{
                  fontSize: 11, height: 24,
                  backgroundColor: isOverridden ? 'rgba(13,29,62,0.09)' : 'rgba(0,0,0,0.06)',
                  '& .MuiChip-deleteIcon': { color: '#888', '&:hover': { color: '#c62828' } },
                }} />
            ))
          : <Typography fontSize={11} color="text.disabled" alignSelf="center">אין אפשרויות</Typography>
        }
      </Stack>

      {/* Add option input */}
      <Stack className="crm-filter-row" direction="row" spacing={0.5} alignItems="center">
        <TextField size="small" value={newOpt} onChange={e => setNewOpt(e.target.value)}
          placeholder="+ הוסף אפשרות..." aria-label="אפשרות חדשה"
          onKeyDown={e => { if (e.key === 'Enter') addOption(); }}
          sx={{
            flex: 1, maxWidth: 180,
            '& .MuiOutlinedInput-root': { fontSize: 11, height: 28, borderRadius: 1.5,
              '& fieldset': { borderColor: 'rgba(0,0,0,0.15)' } },
          }} />
        <IconButton size="small" onClick={addOption}
          disabled={saving || !newOpt.trim()} aria-label="הוספת אפשרות"
          sx={{ color: NAVY, opacity: newOpt.trim() ? 1 : 0.3 }}>
          {saving ? <CircularProgress size={14} /> : <AddIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Stack>

      {isOverridden && (
        <Typography fontSize={10} color="#1565c0"
          sx={{ cursor: 'pointer', mt: 0.8, '&:hover': { textDecoration: 'underline' } }}
          onClick={() => saveOptions([])}
          {...clickableA11y(() => saveOptions([]), { ariaLabel: 'איפוס לאפשרויות ברירת המחדל', asButton: true })}>
          ↩ איפוס לאפשרויות ברירת המחדל
        </Typography>
      )}
    </Box>
  );
};

// ── Attributes panel (right column) ───────────────────────────────────────────
const AttributesPanel = ({ productId }) => {
  const notify = useNotify();
  const [allAttrs,    setAllAttrs]    = useState([]);
  const [linkedIds,   setLinkedIds]   = useState(null);
  const [optionsMap,  setOptionsMap]  = useState({});
  const [pickerValue, setPickerValue] = useState(null);
  const [newAttrOpen, setNewAttrOpen] = useState(false);
  const [linking,     setLinking]     = useState(false);

  useEffect(() => {
    if (!productId) return;
    Promise.all([
      authFetch('/attributes?_start=0&_end=200&_sort=label&_order=ASC').then(r => r.json()),
      authFetch(`/attributes/product-links?product_id=${productId}`).then(r => r.json()),
      authFetch(`/attributes/product-options?product_id=${productId}`).then(r => r.json()),
    ]).then(([all, linked, opts]) => {
      setAllAttrs(Array.isArray(all) ? all : []);
      setLinkedIds(new Set(Array.isArray(linked) ? linked.map(a => Number(a.id)) : []));
      setOptionsMap(opts || {});
    }).catch(() => setLinkedIds(new Set()));
  }, [productId]);

  if (linkedIds === null) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
      <CircularProgress size={24} />
    </Box>
  );

  const linkedAttrs   = allAttrs.filter(a => linkedIds.has(Number(a.id)));
  const unlinkedAttrs = allAttrs.filter(a => !linkedIds.has(Number(a.id)));

  const linkAttr = async (attr) => {
    setLinking(true);
    try {
      const res = await authFetch('/attributes/product-links/add', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, attribute_id: attr.id }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה בהוספת תכונה'), { type: 'error' }); return; }
      setLinkedIds(prev => new Set([...prev, Number(attr.id)]));
      setPickerValue(null);
    } catch { notify('שגיאה בהוספת תכונה', { type: 'error' }); }
    finally { setLinking(false); }
  };

  const unlinkAttr = async (attr) => {
    try {
      const res = await authFetch(`/attributes/product-links/${productId}/${attr.id}`, { method: 'DELETE' });
      if (!res.ok) { notify(await responseError(res, 'שגיאה בהסרת תכונה'), { type: 'error' }); return; }
      setLinkedIds(prev => { const n = new Set(prev); n.delete(Number(attr.id)); return n; });
    } catch { notify('שגיאה בהסרת תכונה', { type: 'error' }); }
  };

  const handleOptionsChange = (attrId, opts) => {
    setOptionsMap(prev => {
      const next = { ...prev };
      if (opts == null) delete next[attrId];
      else next[attrId] = opts;
      return next;
    });
  };

  const handleNewAttrCreated = (attr) => {
    setAllAttrs(prev => [...prev, attr].sort((a, b) => (a.label || '').localeCompare(b.label || '', 'he')));
    linkAttr(attr);
  };

  return (
    <Box>
      {/* Linked attribute cards */}
      {linkedAttrs.length === 0 ? (
        <Box sx={{ p: 3, border: '1.5px dashed rgba(13,29,62,0.18)', borderRadius: 2, mb: 2,
          textAlign: 'center', backgroundColor: 'rgba(13,29,62,0.01)' }}>
          <Typography fontSize={13} color="text.secondary">אין תכונות משויכות</Typography>
          <Typography fontSize={11} color="text.disabled" mt={0.4}>הוסף תכונות מהרשימה למטה</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5} mb={2}>
          {linkedAttrs.map(attr => (
            <AttrCard key={attr.id} attr={attr} productId={productId}
              currentOptions={optionsMap[attr.id] ?? null}
              globalOptions={Array.isArray(attr.options) ? attr.options : []}
              onRemove={() => unlinkAttr(attr)}
              onOptionsChange={handleOptionsChange} />
          ))}
        </Stack>
      )}

      {/* Add attribute row */}
      <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${GOLD}`, backgroundColor: '#fdfaf4' }}>
        <Typography fontSize={11} fontWeight={700} color={NAVY} mb={1.2}>הוסף תכונה</Typography>
        <Stack className="crm-wrap" direction="row" spacing={1} flexWrap="wrap" gap={1}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 160 }}
            size="small"
            options={unlinkedAttrs}
            value={pickerValue}
            onChange={(_, v) => setPickerValue(v)}
            getOptionLabel={o => o.label && o.label !== o.key ? `${o.label} (${o.key})` : (o.label || o.key)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={p => <TextField {...p} placeholder="בחר תכונה קיימת..." sx={inputSx} />}
            noOptionsText={unlinkedAttrs.length === 0 ? 'כל התכונות שויכו' : 'לא נמצאה'}
          />
          <Button variant="contained" size="small"
            onClick={() => pickerValue && linkAttr(pickerValue)}
            disabled={!pickerValue || linking}
            startIcon={linking ? <CircularProgress size={12} color="inherit" /> : null}
            sx={{ backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' }, height: 40 }}>
            הוסף
          </Button>
        </Stack>
        <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => setNewAttrOpen(true)}
          sx={{ mt: 1, fontSize: 12, color: NAVY, borderColor: NAVY, px: 1.5,
            textDecoration: 'underline', textUnderlineOffset: 3 }}>
          צור תכונה חדשה
        </Button>
      </Box>

      <NewAttrDialog open={newAttrOpen} onClose={() => setNewAttrOpen(false)}
        onCreated={handleNewAttrCreated} />
    </Box>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────
const ProductEdit = () => {
  const { id }   = useParams();
  const notify   = useNotify();
  const navigate = useNavigate();

  const [name,       setName]       = useState('');
  const [description,setDescription]= useState('');
  const [isFinished, setIsFinished] = useState(false);
  const [unit,       setUnit]       = useState('יחידה');
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    authFetch(`/products/${id}`)
      .then(r => r.json())
      .then(d => {
        setName(d.name || '');
        setDescription(d.description || '');
        setIsFinished(!!d.is_finished_product);
        setUnit(d.unit || 'יחידה');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    if (!name.trim()) { notify('נא להזין שם קטגוריה', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const res = await authFetch(`/products/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description: description || null, is_finished_product: isFinished, unit }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה בשמירה'), { type: 'error' }); return; }
      notify('הקטגוריה נשמרה', { type: 'success' });
    } catch { notify('שגיאה בשמירה', { type: 'error' }); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box className="crm-page" p={3}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <IconButton onClick={() => navigate(-1)} size="small" aria-label="חזרה"
          sx={{ color: NAVY, border: `1px solid rgba(13,29,62,0.2)`, borderRadius: 1.5 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography {...typo.pageTitle} sx={{ lineHeight: 1.2 }}>
            {name || 'עריכת קטגוריה'}
          </Typography>
          <Typography {...typo.meta}>עריכת קטגוריית מוצר</Typography>
        </Box>
      </Stack>

      {/* Two-column layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3, alignItems: 'start' }}>

        {/* ── Left col: basic info ── */}
        <Card sx={{ ...card.section, mb: 0 }}>
          <CardContent className="crm-card-content" sx={{ p: 3 }}>
            <Typography {...typo.sectionTitle} mb={2.5}>פרטי קטגוריה</Typography>

            <Stack spacing={2.5}>
              <TextField label="שם הקטגוריה *" value={name} onChange={e => setName(e.target.value)}
                fullWidth sx={inputSx} />

              <TextField label="תיאור" value={description} onChange={e => setDescription(e.target.value)}
                fullWidth multiline rows={3} sx={inputSx} />

              <FormControl fullWidth>
                <InputLabel>יחידת מידה</InputLabel>
                <Select value={unit} onChange={e => setUnit(e.target.value)} label="יחידת מידה" sx={inputSx}>
                  {UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox checked={isFinished} onChange={e => setIsFinished(e.target.checked)}
                    sx={{ color: NAVY, '&.Mui-checked': { color: NAVY } }} />
                }
                label={
                  <Box>
                    <Typography fontSize={13} fontWeight={600}>מוצר מוגמר</Typography>
                    <Typography {...typo.meta}>יופיע בייצור ומנות כמוצר לייצור</Typography>
                  </Box>
                }
              />

              <Button variant="contained" onClick={handleSave} disabled={saving} fullWidth
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{ ...btn.primaryLg, mt: 0.5 }}>
                שמור פרטים
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* ── Right col: attributes ── */}
        <Card sx={{ ...card.section, mb: 0 }}>
          <CardContent className="crm-card-content" sx={{ p: 3 }}>
            <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
              <Box>
                <Typography {...typo.sectionTitle}>תכונות קטגוריה</Typography>
                <Typography fontSize={11} color="text.secondary" mt={0.3}>
                  שינויים בתכונות נשמרים אוטומטית
                </Typography>
              </Box>
            </Stack>

            <AttributesPanel productId={id} />
          </CardContent>
        </Card>

      </Box>
    </Box>
  );
};

export default ProductEdit;
